/**
 * Custom sidebar / surround colour.
 *
 * The owner picks ONE background colour (16 Sept 2026 decision); every other
 * sidebar token (text, hover/active highlight, border, ring, primary) is
 * derived here for contrast. Tailwind reads the sidebar palette as
 * `hsl(var(--sidebar-*))` HSL triplets, so the derived values are emitted in
 * that form and set inline on <html> by ThemeContext, overriding whichever
 * colour theme / light-dark block is active.
 */

const HEX_COLOUR = /^#[0-9a-f]{6}$/;

export const SIDEBAR_COLOUR_STORAGE_KEY = 'sidebarColour';
export const SIDEBAR_SWATCHES_STORAGE_KEY = 'sidebarSwatches';

/** Preset swatches shown beside the colour wheel. */
export const SIDEBAR_COLOUR_PRESETS = Object.freeze([
  { name: 'Navy', hex: '#1e2a44' },
  { name: 'Charcoal', hex: '#2b2f36' },
  { name: 'Forest', hex: '#1f3d2e' },
  { name: 'Plum', hex: '#3b2447' },
  { name: 'Teal', hex: '#0f4c5c' },
  { name: 'Sky', hex: '#dbe8f6' },
  { name: 'Sand', hex: '#efe7d6' },
  { name: 'Mint', hex: '#dff1e6' },
  { name: 'Lilac', hex: '#e8e0f3' },
  { name: 'Snow', hex: '#f5f7fa' },
]);

/** The built-in swatch colours, in slot order. */
export const DEFAULT_SIDEBAR_SWATCHES = Object.freeze(SIDEBAR_COLOUR_PRESETS.map((p) => p.hex));

/**
 * The user's own swatch set: ten slots, each a `#rrggbb`. Any slot that is
 * missing or invalid falls back to the built-in colour for that slot, so a
 * partially edited or corrupted stored set still renders ten usable swatches.
 */
export function normaliseSidebarSwatches(value) {
  const list = Array.isArray(value) ? value : [];
  return DEFAULT_SIDEBAR_SWATCHES.map((fallback, i) => normaliseSidebarHex(list[i]) || fallback);
}

export function loadSidebarSwatches(storage) {
  try {
    const raw = storage ? storage.getItem(SIDEBAR_SWATCHES_STORAGE_KEY) : null;
    return normaliseSidebarSwatches(raw ? JSON.parse(raw) : null);
  } catch {
    return [...DEFAULT_SIDEBAR_SWATCHES];
  }
}

export function saveSidebarSwatches(storage, swatches) {
  if (!storage) return;
  const next = normaliseSidebarSwatches(swatches);
  const isDefault = next.every((hex, i) => hex === DEFAULT_SIDEBAR_SWATCHES[i]);
  if (isDefault) storage.removeItem(SIDEBAR_SWATCHES_STORAGE_KEY);
  else storage.setItem(SIDEBAR_SWATCHES_STORAGE_KEY, JSON.stringify(next));
}

export function normaliseSidebarHex(value) {
  const hex = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (HEX_COLOUR.test(hex)) return hex;
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(hex);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return null;
}

export function hexToRgb(hex) {
  const n = normaliseSidebarHex(hex);
  if (!n) return null;
  return {
    r: parseInt(n.slice(1, 3), 16),
    g: parseInt(n.slice(3, 5), 16),
    b: parseInt(n.slice(5, 7), 16),
  };
}

export function rgbToHsl({ r, g, b }) {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rr) h = ((gg - bb) / d + (gg < bb ? 6 : 0)) * 60;
    else if (max === gg) h = ((bb - rr) / d + 2) * 60;
    else h = ((rr - gg) / d + 4) * 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function relativeLuminance({ r, g, b }) {
  const lin = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function isDarkColour(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  return relativeLuminance(rgb) < 0.35;
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const round = (v) => Math.round(v * 10) / 10;

/** `h s% l%` in the form Tailwind's `hsl(var(--token))` expects. */
export function hslTriplet({ h, s, l }) {
  return `${round(((h % 360) + 360) % 360)} ${round(clamp(s, 0, 100))}% ${round(clamp(l, 0, 100))}%`;
}

/**
 * Derive the full sidebar token set from one background colour.
 * Dark backgrounds get near-white text and a slightly lighter highlight;
 * light backgrounds get dark text and a slightly darker highlight. Hue and a
 * touch of the background's saturation carry through so the highlight and
 * border look tinted rather than grey.
 *
 * @param {string} hex - `#rrggbb`
 * @returns {Record<string,string>|null} CSS custom property → HSL triplet
 */
export function deriveSidebarTokens(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb);
  const dark = relativeLuminance(rgb) < 0.35;
  const tint = clamp(s * 0.6, 0, 40);
  const fg = dark
    ? { h, s: clamp(s * 0.25, 0, 20), l: 96 }
    : { h, s: clamp(s * 0.5, 0, 35), l: 16 };
  const accent = dark
    ? { h, s: tint, l: clamp(l + 9, 0, 100) }
    : { h, s: tint, l: clamp(l - 7, 0, 100) };
  const border = dark
    ? { h, s: tint, l: clamp(l + 14, 0, 100) }
    : { h, s: tint, l: clamp(l - 12, 0, 100) };
  const ring = dark
    ? { h, s: clamp(s * 0.4, 0, 40), l: clamp(l + 35, 0, 100) }
    : { h, s: clamp(s * 0.4, 0, 40), l: clamp(l - 35, 0, 100) };
  return {
    '--sidebar-background': hslTriplet({ h, s, l }),
    '--sidebar-foreground': hslTriplet(fg),
    '--sidebar-primary': hslTriplet(fg),
    '--sidebar-primary-foreground': hslTriplet({ h, s, l }),
    '--sidebar-accent': hslTriplet(accent),
    '--sidebar-accent-foreground': hslTriplet(fg),
    '--sidebar-border': hslTriplet(border),
    '--sidebar-ring': hslTriplet(ring),
  };
}

/** `h s% l%` triplet → `#rrggbb` (null when the triplet does not parse). */
export function hslTripletToHex(triplet) {
  const m = /^\s*([0-9.]+)\s+([0-9.]+)%\s+([0-9.]+)%/.exec(String(triplet || ''));
  if (!m) return null;
  const h = Number(m[1]) / 360;
  const s = Number(m[2]) / 100;
  const l = Number(m[3]) / 100;
  const hue = (t) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const toHex = (v) => Math.round(clamp(v, 0, 1) * 255).toString(16).padStart(2, '0');
  if (s === 0) return `#${toHex(l)}${toHex(l)}${toHex(l)}`;
  return `#${toHex(hue(h + 1 / 3))}${toHex(hue(h))}${toHex(hue(h - 1 / 3))}`;
}

export const SIDEBAR_TOKEN_NAMES = Object.freeze([
  '--sidebar-background',
  '--sidebar-foreground',
  '--sidebar-primary',
  '--sidebar-primary-foreground',
  '--sidebar-accent',
  '--sidebar-accent-foreground',
  '--sidebar-border',
  '--sidebar-ring',
]);

/**
 * Apply (or clear, when `hex` is null) the custom sidebar colour on <html>.
 * Inline custom properties win over every stylesheet theme block, so the
 * light/dark mode and colour theme keep driving the main panel only.
 */
export function applySidebarColour(root, hex) {
  if (!root || !root.style) return;
  const tokens = hex ? deriveSidebarTokens(hex) : null;
  if (!tokens) {
    SIDEBAR_TOKEN_NAMES.forEach((name) => root.style.removeProperty(name));
    root.removeAttribute('data-sidebar-colour');
    return;
  }
  Object.entries(tokens).forEach(([name, value]) => root.style.setProperty(name, value));
  root.setAttribute('data-sidebar-colour', isDarkColour(hex) ? 'dark' : 'light');
}

/**
 * The sidebar colour the active theme (light/dark + colour theme) would paint
 * on its own, as `#rrggbb`. With a custom colour applied the inline override
 * hides the stylesheet value, so the inline tokens are lifted for one style
 * read and restored verbatim. Returns null where computed styles are unavailable.
 */
export function readThemeSidebarDefault(root) {
  if (!root || !root.style || typeof window === 'undefined' || !window.getComputedStyle) return null;
  const saved = SIDEBAR_TOKEN_NAMES.map((name) => [name, root.style.getPropertyValue(name)]);
  saved.forEach(([name, value]) => { if (value) root.style.removeProperty(name); });
  let triplet = '';
  try {
    triplet = window.getComputedStyle(root).getPropertyValue('--sidebar-background');
  } finally {
    saved.forEach(([name, value]) => { if (value) root.style.setProperty(name, value); });
  }
  return hslTripletToHex(triplet);
}
